import {computed, signal, type Signal} from './arr-waveform-store';
import {cleanup} from './event-streams';
export interface ScrollData {
  scrollLeft: number;
  scrollWidth: number;
  clientWidth: number;
}
export interface ScrollPercentages {
  startX: number;
  endX: number;
}
export function calculateScrollPercentages(
  scrollData: ScrollData
): ScrollPercentages {
  const {scrollLeft, scrollWidth, clientWidth} = scrollData;
  if (scrollWidth === 0) {
    return {startX: 0, endX: 1};
  }
  const startX = scrollLeft / scrollWidth;
  const endX = (scrollLeft + clientWidth) / scrollWidth;
  return {
    startX: Math.max(0, Math.min(1, startX)),
    endX: Math.max(0, Math.min(1, endX)),
  };
}
export function calculateScrollBounds(scrollData: ScrollData): {
  left: number;
  right: number;
} {
  return {
    left: scrollData.scrollLeft,
    right: scrollData.scrollLeft + scrollData.clientWidth,
  };
}
export interface ScrollStream {
  scrollData: Signal<ScrollData>;
  percentages: Signal<ScrollPercentages>;
  bounds: Signal<{left: number; right: number}>;
  cleanup: () => void;
}
export function createScrollStream(element: HTMLElement): ScrollStream {
  const scrollData = signal<ScrollData>({
    scrollLeft: element.scrollLeft,
    scrollWidth: element.scrollWidth,
    clientWidth: element.clientWidth,
  });
  const percentages = computed(() => {
    return calculateScrollPercentages(scrollData.value);
  }, [scrollData]);
  const bounds = computed(() => {
    return calculateScrollBounds(scrollData.value);
  }, [scrollData]);
  const onScroll = () => {
    scrollData.set({
      scrollLeft: element.scrollLeft,
      scrollWidth: element.scrollWidth,
      clientWidth: element.clientWidth,
    });
  };
  element.addEventListener('scroll', onScroll, {passive: true});
  const cleanupFn = () => {
    element.removeEventListener('scroll', onScroll);
    cleanup(scrollData);
  };
  return {scrollData, percentages, bounds, cleanup: cleanupFn};
}
